/**
 * PasswordInput — every password field on the web portals.
 *
 * An `Input` with an eye button that shows what was typed, so a person can
 * check a password before sending it instead of retyping it blind (NIST
 * 800-63B §5.1.1.2). The button is a real toggle: named for what it will do,
 * `aria-pressed` while the password shows, tied to the field by
 * `aria-controls`, and reachable by keyboard.
 *
 * Like every other text this package renders, the two button names are passed
 * in translated (`t('auth.showPassword')` / `t('auth.hidePassword')`).
 */

import { Eye, EyeOff } from 'lucide-react';
import * as React from 'react';

import { cn } from '../lib/utils';
import { Input, type InputProps } from '../primitives/input';

export interface PasswordInputProps extends Omit<InputProps, 'type'> {
  /** The eye button's accessible names, translated by the caller. */
  revealLabels: { show: string; hide: string };
}

export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ revealLabels, className, id, disabled, ...props }, ref) => {
    const [revealed, setRevealed] = React.useState(false);
    const testId = (props as { 'data-testid'?: string })['data-testid'];

    return (
      <div className="relative">
        <Input
          ref={ref}
          id={id}
          type={revealed ? 'text' : 'password'}
          disabled={disabled}
          className={cn('pr-10', className)}
          {...props}
        />
        <button
          type="button"
          onClick={() => setRevealed((value) => !value)}
          disabled={disabled}
          aria-label={revealed ? revealLabels.hide : revealLabels.show}
          aria-pressed={revealed}
          aria-controls={id}
          data-testid={testId ? `${testId}-reveal` : undefined}
          className={cn(
            'absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md',
            'text-muted-foreground transition-colors hover:text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:pointer-events-none disabled:opacity-50'
          )}
        >
          {revealed ? (
            <EyeOff className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Eye className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';
